/**
 * ポイント取得QRのURL生成・解析ユーティリティ
 */

/** ポイント取得QRの既定ベースURL */
const DEFAULT_POINT_CLAIM_BASE_URL = 'https://ikomasai.local';

/** ポイント取得画面のパス */
const POINT_CLAIM_SCAN_PATH = 'scan';

/** ポイント取得トークンのクエリ名 */
const POINT_CLAIM_TOKEN_QUERY_NAME = 'token';

/** 互換用のトークンクエリ名一覧 */
const POINT_CLAIM_TOKEN_QUERY_NAMES = [POINT_CLAIM_TOKEN_QUERY_NAME, 'code', 't'];

/** 企画ポイント手入力コードで使用する文字 */
export const POINT_CLAIM_MANUAL_CODE_ALPHABET =
  '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';

/** 企画ポイント手入力コードの文字数 */
export const POINT_CLAIM_MANUAL_CODE_LENGTH = 8;

/** QRと同時発行する企画ポイント手入力コードの形式 */
const POINT_CLAIM_MANUAL_CODE_PATTERN = new RegExp(
  `^[${POINT_CLAIM_MANUAL_CODE_ALPHABET}]{${POINT_CLAIM_MANUAL_CODE_LENGTH}}$`,
);

/**
 * 配布用8文字と旧PD-形式を、企画・アンケートから識別する。
 * @param value 判定対象
 * @returns 配布用の手入力コードならtrue
 */
export const isPointDistributionManualCode = (value: unknown): boolean =>
  /^(?:[01][23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{7}|PD-[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{8})$/.test(String(value ?? '').trim().toUpperCase());

/**
 * ポイント取得トークンまたは手入力コードを正規化する
 * @param {string | undefined | null} token - 正規化対象
 * @returns {string} 手入力コードは大文字、それ以外は元の表記を保った値
 */
export const normalizePointClaimToken = (token) => {
  /** 前後空白を除去した候補 */
  const trimmedToken = String(token ?? '').trim();
  /** 大文字へ変換した手入力コード候補 */
  const normalizedManualCode = trimmedToken.toUpperCase();

  return (POINT_CLAIM_MANUAL_CODE_PATTERN.test(normalizedManualCode) || isPointDistributionManualCode(normalizedManualCode))
    ? normalizedManualCode
    : trimmedToken;
};

/**
 * 入力値が企画ポイントの8文字手入力コードか判定する
 * @param {string | undefined | null} value - 判定対象
 * @returns {boolean} 8文字手入力コードの場合true
 */
export const isPointClaimManualCode = (value) => {
  return POINT_CLAIM_MANUAL_CODE_PATTERN.test(normalizePointClaimToken(value));
};

/**
 * URLベース文字列を正規化する
 * @param {string | undefined | null} baseUrl - URLベース
 * @returns {string} 正規化済みURLベース
 */
const normalizeBaseUrl = (baseUrl) => {
  /** 前後空白を除去したURLベース */
  const normalizedBaseUrl = String(baseUrl ?? '').trim();

  if (!normalizedBaseUrl) {
    return DEFAULT_POINT_CLAIM_BASE_URL;
  }

  return normalizedBaseUrl.replace(/\/+$/, '');
};

/**
 * 現在のWeb配信元を取得する
 * @returns {string} Web配信元
 */
const getWebOrigin = () => {
  if (typeof window === 'undefined' || !window.location?.origin) {
    return '';
  }

  if (window.location.origin === 'null' || window.location.protocol === 'file:') {
    return '';
  }

  return window.location.origin;
};

/**
 * QRに埋め込むアプリURLのベースを取得する
 * @returns {string} アプリURLベース
 */
export const getPointClaimQrBaseUrl = () => {
  /** 環境変数で指定された公開アプリURL */
  const configuredBaseUrl = String(process.env.VITE_PUBLIC_APP_BASE_URL ?? '').trim();

  return normalizeBaseUrl(configuredBaseUrl || getWebOrigin());
};

/**
 * ポイント取得QRに埋め込むURLを生成する
 * @param {string} token - ポイント取得トークン
 * @param {string} [baseUrl] - URLベース
 * @returns {string} QR埋め込みURL
 */
export const createPointClaimQrPayload = (token, baseUrl = getPointClaimQrBaseUrl()) => {
  /** 前後空白を除去したトークン */
  const normalizedToken = String(token ?? '').trim();

  /** 正規化済みURLベース */
  const normalizedBaseUrl = normalizeBaseUrl(baseUrl);

  /** URLクエリ */
  const searchParams = new URLSearchParams();
  searchParams.set(POINT_CLAIM_TOKEN_QUERY_NAME, normalizedToken);

  return `${normalizedBaseUrl}/${POINT_CLAIM_SCAN_PATH}?${searchParams.toString()}`;
};

/**
 * URLオブジェクトからポイント取得トークンを抽出する
 * @param {URL} url - 解析対象URL
 * @returns {string} 抽出トークン
 */
const extractTokenFromUrl = (url) => {
  for (const queryName of POINT_CLAIM_TOKEN_QUERY_NAMES) {
    /** クエリから取得したトークン */
    const tokenFromQuery = String(url.searchParams.get(queryName) ?? '').trim();

    if (tokenFromQuery) {
      return tokenFromQuery;
    }
  }

  /** パス末尾から取得するトークン */
  const pathSegments = url.pathname.split('/').filter(Boolean);
  /** scanパスの位置 */
  const scanPathIndex = pathSegments.findIndex((segment) => segment === POINT_CLAIM_SCAN_PATH);

  if (scanPathIndex >= 0 && pathSegments[scanPathIndex + 1]) {
    return decodeURIComponent(pathSegments[scanPathIndex + 1]).trim();
  }

  return '';
};

/**
 * QR読み取り値または手入力値からポイント取得トークンを抽出する
 * @param {string} value - QR読み取り値または手入力値
 * @returns {string} ポイント取得トークン
 */
export const extractPointClaimToken = (value) => {
  /** 前後空白を除去した入力値 */
  const normalizedValue = String(value ?? '').trim();

  if (!normalizedValue) {
    return '';
  }

  try {
    /** URLとして解析した入力値 */
    const parsedUrl = new URL(normalizedValue, DEFAULT_POINT_CLAIM_BASE_URL);
    /** URLから抽出したトークン */
    const tokenFromUrl = extractTokenFromUrl(parsedUrl);

    return normalizePointClaimToken(tokenFromUrl || normalizedValue);
  } catch {
    return normalizePointClaimToken(normalizedValue);
  }
};

/**
 * QR読み取り値がポイント取得用URLか判定する
 * @param {string} value - QR読み取り値
 * @returns ポイント取得用URLならtrue
 */
export const isPointClaimQrPayload = (value) => {
  const normalizedValue = String(value ?? '').trim();
  if (!normalizedValue) {
    return false;
  }

  try {
    const parsedUrl = new URL(normalizedValue);
    const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
    return (
      pathSegments.includes(POINT_CLAIM_SCAN_PATH)
      && Boolean(extractTokenFromUrl(parsedUrl))
    );
  } catch {
    return false;
  }
};
